"use client";

import { useState } from "react";
import Modal from "@/app/components/common/Modal";
import { outboxUnshareSession } from "@/app/lib/outbox";

interface ManageSharingProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
  participants: string[];
}

export default function ManageSharing({ isOpen, onClose, sessionId, participants }: ManageSharingProps) {
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);
  const [removedEmails, setRemovedEmails] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  const handleRemove = async (email: string) => {
    setError(null);
    setRemovingEmail(email);
    // outboxUnshareSession never throws — queues on failure for retry.
    await outboxUnshareSession(sessionId, email);
    setRemovedEmails((prev) => new Set([...prev, email]));
    setRemovingEmail(null);
  };

  const handleStopSharing = async () => {
    setError(null);
    const remaining = participants.filter((p) => !removedEmails.has(p));
    for (const email of remaining) {
      setRemovingEmail(email);
      await outboxUnshareSession(sessionId, email);
      setRemovedEmails((prev) => new Set([...prev, email]));
    }
    setRemovingEmail(null);
  };

  const activeParticipants = participants.filter((p) => !removedEmails.has(p));

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Manage Sharing">
      <div style={{ padding: "0 16px 16px", minWidth: "300px" }}>
        {error && (
          <div style={{
            padding: "8px 12px",
            marginBottom: "12px",
            borderRadius: "var(--radius)",
            background: "rgba(239, 68, 68, 0.15)",
            color: "#ef4444",
            fontSize: "13px",
          }}>
            {error}
          </div>
        )}

        {activeParticipants.length === 0 ? (
          <div style={{ padding: "24px 0", textAlign: "center", color: "var(--text-tertiary)", fontSize: "13px" }}>
            No participants. This chat is no longer shared.
          </div>
        ) : (
          <>
            <div style={{ marginBottom: "16px" }}>
              {activeParticipants.map((email) => (
                <div
                  key={email}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "10px 0",
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <span style={{ fontSize: "14px", color: "var(--text-primary)" }}>
                    {email}
                  </span>
                  <button
                    className="btn-ghost"
                    onClick={() => handleRemove(email)}
                    disabled={removingEmail === email}
                    style={{ padding: "4px 10px", fontSize: "12px", color: "#ef4444" }}
                  >
                    {removingEmail === email ? "Removing..." : "Remove"}
                  </button>
                </div>
              ))}
            </div>

            {activeParticipants.length > 1 && (
              <button
                className="btn-ghost"
                onClick={handleStopSharing}
                disabled={removingEmail !== null}
                style={{
                  width: "100%",
                  padding: "10px",
                  fontSize: "13px",
                  color: "#ef4444",
                  border: "1px solid rgba(239, 68, 68, 0.3)",
                  borderRadius: "var(--radius)",
                }}
              >
                Remove All
              </button>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
